import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { ItemService } from '../../item/item.service';
import { SectionService } from '../../section/section.service';
import { dateAsString } from '../../shared/date.helper';
import { Answer, Inspection, InspectionItem, ItemInfo } from '../../shared/interfaces';
import { VersionService } from '../../version/version.service';
import { AnswerService } from '../answer.service';
import { AnswerStatus } from '../answer.status';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';

@Component({
  selector: 'app-inspection-vendor-overview',
  templateUrl: './inspection-vendor-overview.component.html',
  styleUrls: ['./inspection-vendor-overview.component.scss'],
})
export class InspectionVendorOverviewComponent implements OnInit {
  dateAsString = dateAsString;

  constructor(
    public inspectionService: InspectionService,
    public itemService: ItemService,
    public answerService: AnswerService,
    public sectionService: SectionService,
    public versionService: VersionService,
    public route: ActivatedRoute,
    private toastr: ToastrService,
    public router: Router,
    private dialog: DialogService,
  ) {}

  get selectedItemIndex() {
    return this._selectedItemIndex;
  }
  set selectedItemIndex(i) {
    this._selectedItemIndex = Math.max(0, Math.min(this.items.length - 1, i));
    this.sections = this.items[this._selectedItemIndex].sections;
    this.selectedSectionIndex = 0;
  }
  get selectedSectionIndex() {
    return this._selectedSectionIndex;
  }
  set selectedSectionIndex(i) {
    this._selectedSectionIndex = Math.max(0, Math.min(this.sections.length - 1, i));
    this.questions = this.sections[this._selectedSectionIndex].questions;
    this.selectedQuestionIndex = 0;

    // In case the first question of the section is hidden, move to the next
    if (!this.question.show) {
      this.next();
    }
  }
  get selectedQuestionIndex() {
    return this._selectedQuestionIndex;
  }
  set selectedQuestionIndex(i) {
    this._selectedQuestionIndex = Math.max(0, Math.min(this.questions.length - 1, i));
    this.question = this.questions[this._selectedQuestionIndex];
    this.picturesAnswer = this.question.answer;
    // this.router.navigate(
    //   ['/inspection/review', this.inspection.id, this.selectedItemIndex, this.selectedSectionIndex, this.selectedQuestionIndex]
    // );
  }

  InspectionStatus = InspectionStatus;

  public AnswerStatus = AnswerStatus;

  forceMenu = false;
  inspection: Inspection;
  items = [];
  picturesAnswer;

  _selectedItemIndex: number;

  sections = [];

  _selectedSectionIndex: number;

  questions = [];

  _selectedQuestionIndex: number;

  question = null;

  answers = [];

  isNotLatestVersion = false;
  isCommentsCollapsed = true;
  isFlagsCollapsed = true;

  pReviewed = 0;
  pAccepted = 0;
  nComments = 0;
  nFlags = 0;

  answersWithFlags = [];
  answersWithComments = [];

  ngOnInit() {
    this.route.params.subscribe(async params => {
      if (!this.inspection) {
        this.inspection = await this.inspectionService.get(params.id);
        this.load();
      }
    });
  }

  async load() {
    await this.loadAnswers();
    await this.loadItems();
    this.mergeAnswers();
    this.setQuestionsVisibility();

    this.route.params.subscribe(params => {
      this.selectedItemIndex = 0; // parseInt(params.item, 10);
      this.selectedSectionIndex = 0; // parseInt(params.section, 10);
      this.selectedQuestionIndex = 0; // parseInt(params.question, 10);
    });

    this.items.forEach(i => {
      i.sections.forEach(s => this.setSectionProgress(s));
      this.setItemProgress(i);
    });
    // this.resetQuestion();
  }

  async loadAnswers() {
    this.answers = await this.inspectionService.answers(this.inspection.id);
  }

  async loadItems() {
    this.items = [];

    const items: Array<InspectionItem & { info?: ItemInfo; label?: string; show?: boolean; index?: number }> = (
      await this.versionService.tree(this.inspection.versionId)
    ).items;

    items.forEach(item => {
      if (item.info.quantity === 1) {
        const customName = item.info.names && item.info.names[0];
        const defaultName = item.name;
        const label = customName ? `${customName} (${defaultName})` : defaultName;

        this.items.push({
          ...item,
          label,
        });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          const customName = item.info.names && item.info.names[i];
          const defaultName = `${item.name} # ${i + 1}`;
          const label = customName ? `${customName} (${defaultName})` : defaultName;

          this.items.push({
            // deep copy
            ...JSON.parse(JSON.stringify(item)),
            label,
            index: i + 1,
          });
        }
      }
    });
  }

  getStats() {
    const totalAnswers = this.answers.length;
    this.pReviewed = 0;
    this.pAccepted = 0;
    this.nFlags = 0;
    this.nComments = 0;

    this.answers.forEach(a => {
      if (a.status !== AnswerStatus.NOT_REVIEWED) {
        this.pReviewed++;
      }
      if (a.status === AnswerStatus.ACCEPTED) {
        this.pAccepted++;
      }
      this.nFlags += Array.isArray(a.flags) ? a.flags.length : 0;
      this.nComments += Array.isArray(a.comments) ? a.comments.length : 0;
    });
    this.pAccepted = Math.floor((100 * this.pAccepted) / this.pReviewed);
    this.pReviewed = Math.floor((100 * this.pReviewed) / totalAnswers);

    this.answersWithFlags = this.answers
      .filter(a => Array.isArray(a.flags) && a.flags.length)
      .map(a => {
        a.path = this.getAnswerURL(a);
        return a;
      });
    this.answersWithComments = this.answers
      .filter(a => Array.isArray(a.comments) && a.comments.length)
      .map(a => {
        a.path = this.getAnswerURL(a);
        return a;
      });
  }

  removeComment(answer, commentIndex) {
    this.dialog.confirm().show({
      message: `Do you really want to remove the Comment?`,
      confirmed: async () => {
        const newAnswer = await this.answerService.removeComment(answer.id, commentIndex);
        answer.comments = newAnswer.comments;
        this.getStats();
        this.toastr.success('Comment removed!');
      },
    });
  }

  // tslint:disable: forin
  getAnswerURL(answer: Answer) {
    const { questionId, buildingIndex } = answer;

    for (const i in this.items) {
      for (const s in this.items[i].sections) {
        for (const q in this.items[i].sections[s].questions) {
          if (this.items[i].sections[s].questions[q].id === questionId) {
            if ('index' in this.items[i]) {
              if (this.items[i].index === buildingIndex) {
                return {
                  question: this.items[i].sections[s].questions[q],
                  iQuestion: q,
                  iSection: s,
                  iItem: i,
                };
              }
            } else {
              return {
                question: this.items[i].sections[s].questions[q],
                iQuestion: q,
                iSection: s,
                iItem: i,
              };
            }
          }
        }
      }
    }
  }

  questionNComments(question) {
    if (Array.isArray(question.answer)) {
      let sum = 0;
      question.answer.forEach(a => {
        sum += a.comments ? a.comments.length : 0;
      });
      return sum;
    }
    return question.answer.comments.length;
  }

  // tslint:disable: forin
  mergeAnswers() {
    for (const iItem in this.items) {
      const sections = this.items[iItem].sections;
      const buildingIndex = this.items[iItem].index;

      for (const iSection in sections) {
        const questions = sections[iSection].questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];

          switch (question.repeat) {
            case 'never':
              const answer = this.answers.find(ans => {
                const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
                return ans.questionId === questions[iQuestion].id && indexOk;
              });

              if (answer) {
                questions[iQuestion].answer = answer;
              } else {
                // console.log('Answer not found for question #', questions[iQuestion].id, buildingIndex);
              }
              break;

            case 'poultry-house':
              const answersPH = this.answers.filter(ans => {
                return ans.questionId === questions[iQuestion].id;
              });

              questions[iQuestion].answer = answersPH;

              // if (answersPH.length !== this.inspection.numberPoultryHouses) {
              //   // console.log(this.inspection.numberPoultryHouses - answersPH.length,
              //   //   'answers not found for question #', questions[iQuestion].id, 'PH');
              // }

              break;

            case 'generator-building':
              const answersGB = this.answers.filter(ans => {
                return ans.questionId === questions[iQuestion].id;
              });

              questions[iQuestion].answer = answersGB;

              // if (answersGB.length !== this.inspection.numberGeneratorBuildings) {
              // console.log(this.inspection.numberGeneratorBuildings - answersPH.length,
              //   'answers not found for question #', questions[iQuestion].id, 'GH');
              // }

              break;

            case 'litter-shed':
              const answersLS = this.answers.filter(ans => {
                return ans.questionId === questions[iQuestion].id;
              });

              questions[iQuestion].answer = answersLS;

              break;

            case 'dwelling':
              const answersD = this.answers.filter(ans => {
                return ans.questionId === questions[iQuestion].id;
              });

              questions[iQuestion].answer = answersD;

              break;

            case 'both':
              const answersBoth = this.answers.filter(ans => {
                return ans.questionId === questions[iQuestion].id;
              });

              questions[iQuestion].answer = answersBoth;

              // const total = this.inspection.numberGeneratorBuildings + this.inspection.numberPoultryHouses;
              // if (answersBoth.length !== total) {
              // console.log(total - answersBoth.length, 'answers not found for question #', questions[iQuestion].id, 'Both');
              // }

              break;
          }
        }
      }
    }
    this.getStats();
  }

  // tslint:disable: forin
  setQuestionsVisibility() {
    for (const iItem in this.items) {
      const sections = this.items[iItem].sections;
      const buildingIndex = this.items[iItem].index;

      for (const iSection in sections) {
        const questions = sections[iSection].questions;

        for (const iQuestion in questions) {
          switch (questions[iQuestion].adminVisibility) {
            case 'always':
              questions[iQuestion].show = true;
              break;

            case 'conditional':
              questions[iQuestion].show = true;
              break;
          }
        }
      }
    }
  }

  next() {
    // Increment question
    if (this.selectedQuestionIndex + 1 < this.questions.length) {
      this.selectedQuestionIndex++;
    } else {
      // Increment section
      if (this.selectedSectionIndex + 1 < this.sections.length) {
        this.selectedSectionIndex++;
      } else {
        // Increment item
        if (this.selectedItemIndex + 1 < this.items.length) {
          this.selectedItemIndex++;
        } else {
          // No questions left
          console.log('the end');

          // Move back to last displayable question
          if (!this.question.show) {
            this.previous();
          }
        }
      }
    }

    // Skip hidden questions
    if (!this.question.show) {
      this.next();
    }

    // this.resetQuestion();
  }

  previous() {
    // Decrement question
    if (this.selectedQuestionIndex) {
      this.selectedQuestionIndex--;
    } else {
      // Decrement section
      if (this.selectedSectionIndex) {
        this.selectedSectionIndex--;
        this.selectedQuestionIndex = this.sections[this.selectedSectionIndex].questions.length - 1;
      } else {
        // Increment item
        if (this.selectedItemIndex) {
          this.selectedItemIndex--;
          this.selectedSectionIndex = this.items[this.selectedItemIndex].sections.length - 1;
          this.selectedQuestionIndex = this.sections[this.selectedSectionIndex].questions.length - 1;
        } else {
          // No questions left
          console.log('the end');

          // Move back to last displayable question
          if (!this.question.show) {
            this.next();
          }
        }
      }
    }

    // Skip hidden questions
    if (!this.question.show) {
      this.previous();
    }

    // this.resetQuestion();
  }

  setSectionProgress(section) {
    let count = 0;

    section.questions.forEach(q => {
      if (Array.isArray(q.answer)) {
        const allRejected = q.answer.reduce((acc, curr) => acc && curr.status === AnswerStatus.REJECTED, true);
        if (allRejected) {
          count++;
        }
      } else {
        if (q.answer.status === AnswerStatus.REJECTED) {
          count++;
        }
      }
    });

    section.progress = ((section.questions.length - count) / section.questions.length) * 100;
  }

  setItemProgress(item) {
    let sum = 0;

    item.sections.forEach(s => {
      sum += s.progress || 0;
    });

    item.progress = sum / item.sections.length;
  }

  getInspectionProgress() {
    if (this.items && this.items.length) {
      let sum = 0;
      this.items.forEach(i => {
        sum += i.progress || 0;
      });

      return sum / this.items.length;
    }
    return 0;
  }

  questionIcon(q) {
    if (Array.isArray(q.answer)) {
      // Multi answer
      const hasFlags = q.answer.flags && q.answer.flags.length;
      if (hasFlags) {
        return 'fa-flag';
      }

      const hasReject = q.answer.reduce((acc, curr) => acc || curr.status === AnswerStatus.REJECTED, false);
      if (hasReject) {
        return 'fa-close';
      }

      const allAccept = q.answer.reduce((acc, curr) => acc && curr.status === AnswerStatus.ACCEPTED, true);
      if (allAccept) {
        return 'fa-check';
      }

      return 'fa-circle-o';
    } else {
      // Single answer
      if (q.answer && Array.isArray(q.answer.flags) && q.answer.flags.length) {
        return 'fa-flag';
      }
      if (q.answer.status === AnswerStatus.ACCEPTED) {
        return 'fa-check';
      }
      if (q.answer.status === AnswerStatus.REJECTED) {
        return 'fa-close';
      }
      return 'fa-circle-o';
    }
  }
}
